/**
 * Rax Agent Quick Add
 *
 * Fast, simple agent creation modal.
 * User describes what agent should do OR picks a template.
 * Matches PRD §19 "+Agent: Quick Add to the Band"
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

interface QuickTemplate {
  id: string;
  icon: string;
  name: string;
  description: string;
  skills: string[];
}

const QUICK_TEMPLATES: QuickTemplate[] = [
  {
    id: "email-assistant",
    icon: "📧",
    name: "Email Assistant",
    description: "Triage inbox, draft replies, and summarize threads",
    skills: ["gmail", "memory", "web_search"],
  },
  {
    id: "data-analyst",
    icon: "📊",
    name: "Data Analyst",
    description: "Research topics, analyze data, and create reports",
    skills: ["web_search", "browser", "files", "code", "memory"],
  },
  {
    id: "code-helper",
    icon: "💻",
    name: "Code Helper",
    description: "Write code, review PRs, and debug issues",
    skills: ["code", "terminal", "files", "memory", "web_search"],
  },
  {
    id: "call-screener",
    icon: "📞",
    name: "Call Screener",
    description: "Handle incoming calls and take messages",
    skills: ["memory", "calendar", "gmail"],
  },
];

@customElement("rax-agent-quick-add")
export class RaxAgentQuickAdd extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        animation: fadeIn 0.2s ease-out;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .panel {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        width: 100%;
        max-width: 420px;
        padding: var(--space-6);
        animation: slideUp 0.3s ease-out;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .title {
        font-size: var(--font-xl);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .title::before {
        content: "🎸";
      }
      
      .close-btn {
        background: transparent;
        border: none;
        color: var(--text-tertiary);
        font-size: 1.5rem;
        cursor: pointer;
        padding: var(--space-1);
        line-height: 1;
      }
      
      .close-btn:hover {
        color: var(--text-primary);
      }
      
      .description-section {
        margin-bottom: var(--space-5);
      }
      
      .section-label {
        font-size: var(--font-md);
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: var(--space-3);
      }
      
      .description-input {
        width: 100%;
        min-height: 80px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-md);
        color: var(--text-primary);
        resize: none;
        font-family: inherit;
      }
      
      .description-input:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .description-input::placeholder {
        color: var(--text-tertiary);
      }
      
      .divider {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin: var(--space-5) 0;
        color: var(--text-tertiary);
        font-size: var(--font-sm);
      }
      
      .divider::before,
      .divider::after {
        content: "";
        flex: 1;
        height: 1px;
        background: var(--border-color);
      }
      
      .templates-section {
        margin-bottom: var(--space-5);
      }
      
      .template-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
      }
      
      .template-card {
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: left;
      }
      
      .template-card:hover {
        border-color: var(--text-tertiary);
        background: var(--bg-hover);
      }
      
      .template-card.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
      }
      
      .template-icon {
        font-size: 1.5rem;
        margin-bottom: var(--space-2);
      }
      
      .template-name {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .template-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        line-height: 1.4;
      }
      
      .create-btn {
        width: 100%;
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-4);
        font-size: var(--font-lg);
        font-weight: 600;
        color: #000;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .create-btn:hover:not(:disabled) {
        background: var(--rax-yellow-hover);
        transform: scale(1.02);
      }
      
      .create-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .hint {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-align: center;
        margin-top: var(--space-3);
      }
      
      @media (max-width: 480px) {
        .panel {
          max-width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
        }
      
        .template-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ];

  @property({ type: Boolean })
  open = false;

  @state()
  private description = "";

  @state()
  private selectedTemplate: string | null = null;

  @state()
  private creating = false;

  override updated(changed: Map<string, unknown>) {
    if (changed.has("open") && this.open) {
      // Reset state when opening
      this.description = "";
      this.selectedTemplate = null;
    }
  }

  private selectTemplate(templateId: string) {
    if (this.selectedTemplate === templateId) {
      this.selectedTemplate = null;
    } else {
      this.selectedTemplate = templateId;
      // Clear description when selecting template
      this.description = "";
    }
  }

  private handleDescriptionInput(e: Event) {
    this.description = (e.target as HTMLTextAreaElement).value;
    // Clear template selection when typing
    if (this.description.trim()) {
      this.selectedTemplate = null;
    }
  }

  private get canCreate(): boolean {
    return !!(this.description.trim() || this.selectedTemplate);
  }

  private async handleCreate() {
    if (!this.canCreate) return;

    this.creating = true;

    try {
      let name: string;
      let description: string;
      let skills: string[];

      if (this.selectedTemplate) {
        const template = QUICK_TEMPLATES.find((t) => t.id === this.selectedTemplate);
        if (!template) return;

        name = template.name;
        description = template.description;
        skills = template.skills;
      } else {
        // Generate name from description
        const words = this.description.trim().split(" ").slice(0, 2);
        name = words.join(" ") + " Agent";
        description = this.description.trim();
        // Default to all capabilities
        skills = [
          "memory",
          "web_search",
          "gmail",
          "calendar",
          "browser",
          "code",
          "files",
          "terminal",
        ];
      }

      const response = await raxApi.createAgent({
        name,
        description,
        type: "custom",
        icon: "🤖",
        skills,
      });

      if (response.success && response.data) {
        this.dispatchEvent(
          new CustomEvent("agent-created", {
            detail: { agent: response.data },
            bubbles: true,
            composed: true,
          }),
        );
        this.handleClose();
      } else {
        console.error("[AgentQuickAdd] Failed to create:", response.error);
      }
    } catch (err) {
      console.error("[AgentQuickAdd] Error creating agent:", err);
    } finally {
      this.creating = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (!this.open) return nothing;

    return html`
      <div class="overlay" @click=${(e: Event) => e.target === e.currentTarget && this.handleClose()}>
        <div class="panel">
          <div class="header">
            <h2 class="title">Add to your band</h2>
            <button class="close-btn" @click=${this.handleClose}>&times;</button>
          </div>

          <div class="description-section">
            <div class="section-label">What should they do?</div>
            <textarea
              class="description-input"
              placeholder="Handle my customer support emails..."
              .value=${this.description}
              @input=${this.handleDescriptionInput}
            ></textarea>
          </div>

          <div class="divider">OR pick a template</div>

          <div class="templates-section">
            <div class="template-grid">
              ${QUICK_TEMPLATES.map(
                (template) => html`
                  <button
                    class="template-card ${this.selectedTemplate === template.id ? "selected" : ""}"
                    @click=${() => this.selectTemplate(template.id)}
                  >
                    <div class="template-icon">${template.icon}</div>
                    <div class="template-name">${template.name}</div>
                    <div class="template-desc">${template.description}</div>
                  </button>
                `,
              )}
            </div>
          </div>

          <button class="create-btn" ?disabled=${!this.canCreate || this.creating} @click=${this.handleCreate}>
            ${this.creating ? "Creating..." : "Create Agent"}
          </button>

          <p class="hint">Your agent will spawn and walk to their desk</p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-agent-quick-add": RaxAgentQuickAdd;
  }
}
